import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Package, School as SchoolIcon, Store,
  ShoppingBag, Wallet, TrendingUp, TrendingDown, Download,
  RefreshCw, Search, X, DollarSign, Receipt, AlertTriangle, CheckCircle2, ArrowRight,
  Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/super/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/super/schools", label: "Schools", icon: SchoolIcon },
  { to: "/super/products", label: "Products", icon: Package },
  { to: "/super/vendors", label: "Vendors", icon: Store },
  { to: "/super/orders", label: "Orders & Ops", icon: ShoppingBag },
  { to: "/super/finance", label: "Finance", icon: Wallet },
  { to: "/super/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/super/settings", label: "Settings", icon: SettingsIcon },
];

const BUCKETS = ["current", "0-30", "31-60", "61-90", "90+"];
const BUCKET_LABEL = {
  "current": "Not due yet", "0-30": "0–30 days", "31-60": "31–60 days",
  "61-90": "61–90 days", "90+": "90+ days",
};
const BUCKET_COLOR = {
  "current": "bg-slate-50 text-slate-700 border-slate-200",
  "0-30":    "bg-amber-50 text-amber-800 border-amber-200",
  "31-60":   "bg-orange-50 text-orange-800 border-orange-200",
  "61-90":   "bg-red-50 text-red-800 border-red-200",
  "90+":     "bg-red-100 text-red-900 border-red-300 font-bold",
};

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function Finance() {
  const [tab, setTab] = useState("receivables");
  const [kpis, setKpis] = useState(null);
  const [rec, setRec] = useState(null);
  const [pay, setPay] = useState(null);
  const [cash, setCash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingVi, setPayingVi] = useState(null); // vendor_invoice for supplier-payment modal

  const load = async () => {
    setLoading(true);
    try {
      const [k, r, p, c] = await Promise.all([
        api.get("/admin/finance/kpis"),
        api.get("/admin/receivables"),
        api.get("/admin/payables"),
        api.get("/admin/cashflow"),
      ]);
      setKpis(k.data); setRec(r.data); setPay(p.data); setCash(c.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const kpiCards = kpis && [
    { k: "outstanding_receivables", label: "Outstanding Receivables",
      value: fmtINR(kpis.outstanding_receivables), icon: TrendingUp, color: "green",
      hint: `${rec?.count || 0} invoices unpaid` },
    { k: "outstanding_payables", label: "Outstanding Payables",
      value: fmtINR(kpis.outstanding_payables), icon: TrendingDown, color: "red",
      hint: `${pay?.count || 0} vendor invoices` },
    { k: "net_working_capital", label: "Net Working Capital",
      value: fmtINR(kpis.net_working_capital), icon: Wallet,
      color: (kpis.net_working_capital >= 0 ? "blue" : "amber"),
      hint: "Receivables − Payables" },
    { k: "expected_cash_30d", label: "Expected Cash (30 d)",
      value: fmtINR(kpis.expected_cash_30d), icon: DollarSign,
      color: (kpis.expected_cash_30d >= 0 ? "green" : "red"),
      hint: "Net position in 30 days" },
  ];

  return (
    <DashboardLayout nav={NAV} title="Finance">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-2xl font-heading font-semibold text-brand-navy" data-testid="finance-title">
            Finance Operations
          </div>
          <div className="text-sm text-slate-500">Working-capital visibility for the pilot</div>
        </div>
        <button onClick={load}
          data-testid="finance-refresh"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 text-sm hover:border-slate-400">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {loading && Array(4).fill(0).map((_, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 h-24 animate-pulse" />
        ))}
        {!loading && kpiCards && kpiCards.map((c) => (
          <div key={c.k} data-testid={`kpi-${c.k}`}
            className="rounded-xl border border-slate-200 bg-white p-4">
            <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-${c.color}-700`}>
              <c.icon size={12} /> {c.label}
            </div>
            <div className={`mt-1.5 text-2xl font-heading font-bold text-${c.color}-800`}>{c.value}</div>
            <div className="text-[11px] text-slate-500 mt-1">{c.hint}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-md bg-slate-100 p-1 text-sm mb-4">
        {[
          { k: "receivables", l: "Receivables" },
          { k: "payables", l: "Payables" },
          { k: "cashflow", l: "Cash Flow" },
        ].map((t) => (
          <button key={t.k} data-testid={`finance-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-1.5 rounded ${tab === t.k ? "bg-white shadow font-semibold text-brand-navy" : "text-slate-600 hover:text-slate-900"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "receivables" && rec && (
        <AgingSection data={rec} kind="receivables" onRefresh={load} />
      )}
      {tab === "payables" && pay && (
        <AgingSection data={pay} kind="payables" onRefresh={load}
          onPay={(row) => setPayingVi(row)} />
      )}
      {tab === "cashflow" && cash && (
        <CashflowSection cash={cash} />
      )}

      {payingVi && (
        <SupplierPayModal
          vi={payingVi}
          onClose={() => setPayingVi(null)}
          onSaved={() => { setPayingVi(null); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

function AgingSection({ data, kind, onRefresh, onPay }) {
  const [q, setQ] = useState("");
  const [bucketFilter, setBucketFilter] = useState("");
  const filtered = useMemo(() => {
    let rows = data.rows;
    if (bucketFilter) rows = rows.filter((r) => r.bucket === bucketFilter);
    if (q.trim()) {
      const n = q.toLowerCase();
      rows = rows.filter((r) =>
        [r.invoice_no, r.school_name, r.vendor_name, r.order_id].some((v) => (v || "").toLowerCase().includes(n))
      );
    }
    return rows;
  }, [data, q, bucketFilter]);

  return (
    <>
      {/* Bucket strip */}
      <div className="grid grid-cols-5 gap-2 mb-4" data-testid={`${kind}-buckets`}>
        {BUCKETS.map((b) => {
          const total = data.totals[b] || 0;
          const count = data.counts[b] || 0;
          const active = bucketFilter === b;
          return (
            <button key={b}
              data-testid={`bucket-${kind}-${b}`}
              onClick={() => setBucketFilter(active ? "" : b)}
              className={`rounded-lg border p-3 text-left transition-all hover:shadow-sm ${BUCKET_COLOR[b]} ${active ? "ring-2 ring-offset-1 ring-brand-blue" : ""}`}>
              <div className="text-[11px] uppercase tracking-wide font-semibold">{BUCKET_LABEL[b]}</div>
              <div className="mt-1 text-lg font-heading font-bold">{fmtINR(total)}</div>
              <div className="text-[11px] opacity-70">{count} {count === 1 ? "invoice" : "invoices"}</div>
            </button>
          );
        })}
      </div>

      <div className="sl-card p-4 mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={`${kind}-search`}
            placeholder={`Search invoice, ${kind === "receivables" ? "school" : "vendor"}, order id…`}
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 pr-4 py-2 w-full rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue"
          />
        </div>
        <a href={`${API_BASE}/admin/${kind}/export.xlsx`}
          data-testid={`${kind}-export`}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark">
          <Download size={13} /> Export Excel
        </a>
      </div>

      <div className="sl-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">Invoice</th>
              <th className="text-left px-4 py-3">{kind === "receivables" ? "School" : "Vendor"}</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Paid</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="text-left px-4 py-3">Due</th>
              <th className="text-left px-4 py-3">Aging</th>
              <th className="text-center px-4 py-3">Status</th>
              {kind === "payables" && <th className="text-right px-4 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={kind === "payables" ? 9 : 8} className="text-center py-12 text-slate-500" data-testid={`${kind}-empty`}>
                No invoices {q || bucketFilter ? "match" : "outstanding"}.
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`${kind}-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">{r.invoice_no}</div>
                  <div className="text-[11px] text-slate-500">Order {(r.order_id || "").slice(0, 8)}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{r.school_name || r.vendor_name || "—"}</td>
                <td className="px-4 py-3 text-right text-slate-700">{fmtINR(r.total)}</td>
                <td className="px-4 py-3 text-right text-slate-500">{fmtINR(r.paid_amount)}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtINR(r.balance)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{(r.due_date || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border ${BUCKET_COLOR[r.bucket]}`}>
                    {r.bucket === "current" ? "Not due" : `${r.days_overdue}d overdue`}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    r.status === "paid" ? "bg-brand-greenSoft text-brand-greenDark" :
                    r.status === "partial" ? "bg-amber-50 text-amber-800" :
                    r.status === "verified" ? "bg-blue-50 text-blue-800" :
                    "bg-slate-100 text-slate-700"}`}>{r.status}</span>
                </td>
                {kind === "payables" && (
                  <td className="px-4 py-3 text-right">
                    {r.status === "submitted" ? (
                      <button
                        data-testid={`verify-${r.id}`}
                        onClick={async () => {
                          try {
                            await api.patch(`/admin/vendor-invoices/${r.id}/verify`);
                            toast.success("Invoice verified");
                            onRefresh();
                          } catch (err) { toast.error(formatApiError(err)); }
                        }}
                        className="px-3 py-1.5 rounded-md border border-slate-300 text-xs hover:border-slate-400">
                        Verify
                      </button>
                    ) : (r.status === "verified" || r.status === "partial") ? (
                      <button
                        data-testid={`pay-${r.id}`}
                        onClick={() => onPay(r)}
                        className="px-3 py-1.5 rounded-md bg-brand-blue text-white text-xs font-semibold hover:bg-brand-blueDark inline-flex items-center gap-1">
                        <ArrowRight size={11} /> Pay
                      </button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                <td colSpan={4} className="px-4 py-3 text-right text-slate-600 uppercase text-xs">
                  Total outstanding
                </td>
                <td className="px-4 py-3 text-right text-brand-navy text-base">
                  {fmtINR(filtered.reduce((s, r) => s + r.balance, 0))}
                </td>
                <td colSpan={kind === "payables" ? 4 : 3}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

function CashflowSection({ cash }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="cashflow-summary">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Net position — next 90 days</div>
        <div className={`mt-2 text-4xl font-heading font-bold ${cash.net_position_90d >= 0 ? "text-brand-greenDark" : "text-red-700"}`}>
          {fmtINR(cash.net_position_90d)}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Inflows expected − Outflows expected. Non-binding forecast based on invoice due dates.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <TrendingUp size={16} className="text-brand-greenDark" />
            <div className="font-heading font-semibold text-brand-navy">Cash IN (school payments)</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2">Horizon</th>
                <th className="text-right px-4 py-2">Invoices</th>
                <th className="text-right px-4 py-2">Amount due</th>
              </tr>
            </thead>
            <tbody>
              {cash.inflows.map((r) => (
                <tr key={r.days} className="border-t border-slate-100" data-testid={`cashflow-in-${r.days}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">Next {r.days} days</td>
                  <td className="px-4 py-3 text-right text-slate-600">{r.count}</td>
                  <td className="px-4 py-3 text-right font-semibold text-brand-greenDark">{fmtINR(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <TrendingDown size={16} className="text-red-700" />
            <div className="font-heading font-semibold text-brand-navy">Cash OUT (supplier payments)</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2">Horizon</th>
                <th className="text-right px-4 py-2">Invoices</th>
                <th className="text-right px-4 py-2">Amount due</th>
              </tr>
            </thead>
            <tbody>
              {cash.outflows.map((r) => (
                <tr key={r.days} className="border-t border-slate-100" data-testid={`cashflow-out-${r.days}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">Next {r.days} days</td>
                  <td className="px-4 py-3 text-right text-slate-600">{r.count}</td>
                  <td className="px-4 py-3 text-right font-semibold text-red-700">{fmtINR(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SupplierPayModal({ vi, onClose, onSaved }) {
  const [form, setForm] = useState({
    amount: vi.balance,
    method: "bank",
    reference: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/vendor-invoices/${vi.id}/record-payment`, {
        amount: Number(form.amount),
        method: form.method,
        reference: form.reference,
        notes: form.notes,
      });
      toast.success("Payment recorded");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="supplier-pay-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-greenDark font-semibold">Record supplier payment</div>
            <div className="mt-1 font-heading font-semibold text-brand-navy">{vi.vendor_name}</div>
            <div className="text-xs text-slate-500">Invoice {vi.invoice_no} · balance {fmtINR(vi.balance)}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <label className="text-xs font-semibold text-slate-600">Amount (₹) *</label>
            <input required type="number" step="0.01" min="0" max={vi.balance + 0.01}
              data-testid="supplier-pay-amount"
              value={form.amount} onChange={(e) => setForm((s) => ({ ...s, amount: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Method</label>
            <select value={form.method} onChange={(e) => setForm((s) => ({ ...s, method: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 bg-white">
              <option value="bank">Bank transfer</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Reference / UTR</label>
            <input value={form.reference} onChange={(e) => setForm((s) => ({ ...s, reference: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
          </div>
        </div>
        <div className="p-5 border-t border-slate-100 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
          <button type="submit" disabled={busy}
            data-testid="supplier-pay-confirm"
            className="px-4 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark disabled:opacity-60">
            {busy ? "Saving…" : "Record payment"}
          </button>
        </div>
      </form>
    </div>
  );
}
